import React, { useEffect, useState, useCallback } from 'react';
import { analyticsAPI } from '../api';

const FMT_TIME = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch (e) { return ''; }
};

const RELATIVE_AGO = (iso) => {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.max(0, Math.floor(ms / 1000));
    if (s < 30) return 'just now';
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return FMT_TIME(iso);
  } catch (e) { return ''; }
};

const MetricCard = ({ label, value, sub, accent, testId }) => (
  <div
    data-testid={testId}
    style={{
      background: '#ffffff',
      border: '1px solid var(--kahla-border)',
      padding: '22px 22px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minHeight: 130,
      position: 'relative',
    }}
  >
    {accent && (
      <span style={{
        position: 'absolute', top: 18, right: 18,
        width: 6, height: 6, borderRadius: '50%',
        background: accent,
        boxShadow: `0 0 0 4px ${accent}1a`,
      }} />
    )}
    <p style={{
      fontSize: 10,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: 'var(--kahla-text-light)',
      margin: 0,
    }}>
      {label}
    </p>
    <p style={{
      fontFamily: 'Playfair Display, serif',
      fontSize: 38,
      lineHeight: 1.1,
      color: 'var(--kahla-text)',
      margin: '4px 0 0',
      fontWeight: 400,
    }}>
      {value}
    </p>
    {sub && (
      <p style={{
        fontSize: 11,
        color: 'var(--kahla-text-light)',
        margin: 0,
        lineHeight: 1.6,
      }}>
        {sub}
      </p>
    )}
  </div>
);

const PageMetricCard = ({ label, data, testId }) => (
  <div
    data-testid={testId}
    style={{
      background: '#ffffff',
      border: '1px solid var(--kahla-border)',
      padding: '22px 22px 18px',
      minHeight: 130,
    }}
  >
    <p style={{
      fontSize: 10,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: 'var(--kahla-text-light)',
      margin: '0 0 14px',
    }}>{label}</p>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 8,
    }}>
      {[
        { k: 'today', l: 'Today' },
        { k: 'last_7_days', l: '7 days' },
        { k: 'total', l: 'Total' },
      ].map(({ k, l }) => (
        <div key={k}>
          <p style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 22,
            color: 'var(--kahla-text)',
            margin: 0,
            lineHeight: 1.1,
          }}>
            {data?.[k] ?? '—'}
          </p>
          <p style={{
            fontSize: 9,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            margin: '4px 0 0',
          }}>{l}</p>
        </div>
      ))}
    </div>
  </div>
);

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await analyticsAPI.overview();
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [load]);

  if (loading && !data) {
    return (
      <p style={{ color: 'var(--kahla-text-light)', fontSize: 12, padding: '40px 0', textAlign: 'center' }}>
        Loading analytics…
      </p>
    );
  }
  if (error) {
    return (
      <p style={{ color: '#a85a5a', fontSize: 12, padding: '40px 0', textAlign: 'center' }}>
        {error}
      </p>
    );
  }

  const activeNow = data?.active_now || { count: 0, users: [] };
  const activeToday = data?.active_today ?? 0;
  const home = data?.homepage_visits || {};
  const req = data?.request_access_visits || {};
  const subs = data?.request_access_submissions || {};

  return (
    <div data-testid="admin-analytics" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 22,
          margin: 0,
          color: 'var(--kahla-text)',
        }}>
          Analytics
        </h2>
        <p style={{
          fontSize: 12,
          color: 'var(--kahla-text-light)',
          margin: '6px 0 0',
        }}>
          Live activity & public page interest. Refreshes every 30 seconds.
        </p>
      </div>

      {/* === Live + today metrics === */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
      }}>
        <MetricCard
          testId="metric-active-now"
          label="Active now"
          value={activeNow.count}
          sub="Last 5 minutes"
          accent={activeNow.count > 0 ? '#3a7a52' : null}
        />
        <MetricCard
          testId="metric-active-today"
          label="Active today"
          value={activeToday}
          sub="Unique users since 00:00 UTC"
        />
      </div>

      {/* === Page metrics === */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
      }}>
        <PageMetricCard testId="metric-homepage" label="Homepage visits" data={home} />
        <PageMetricCard testId="metric-request-page" label="Request access page visits" data={req} />
        <PageMetricCard testId="metric-submissions" label="Requests submitted" data={subs} />
      </div>

      {/* === Active now list === */}
      <div style={{
        background: '#ffffff',
        border: '1px solid var(--kahla-border)',
        padding: '22px 22px 8px',
      }}>
        <p style={{
          fontSize: 10,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--kahla-text-light)',
          margin: '0 0 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: activeNow.count > 0 ? '#3a7a52' : 'var(--kahla-border)',
            boxShadow: activeNow.count > 0 ? '0 0 0 3px #3a7a521a' : 'none',
          }} />
          Currently active
        </p>
        {activeNow.users.length === 0 ? (
          <p style={{
            fontSize: 12, color: 'var(--kahla-text-light)',
            fontStyle: 'italic', margin: 0, padding: '8px 0 16px',
          }}>
            No users active in the last 5 minutes.
          </p>
        ) : (
          <ul
            data-testid="active-now-list"
            style={{ listStyle: 'none', padding: 0, margin: 0 }}
          >
            {activeNow.users.map((u) => (
              <li
                key={u.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--kahla-cream)',
                  gap: 12,
                  fontSize: 13,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: '1 1 auto' }}>
                  <span style={{ color: 'var(--kahla-text)', fontWeight: 500 }}>
                    {u.workspace_name || u.username}
                    {u.role === 'admin' && (
                      <span style={{
                        fontSize: 9, letterSpacing: '0.12em',
                        marginLeft: 8, color: 'var(--kahla-text-light)',
                        textTransform: 'uppercase',
                      }}>· admin</span>
                    )}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--kahla-text-light)' }}>
                    {u.email}
                  </span>
                </div>
                <span style={{
                  fontSize: 11,
                  color: 'var(--kahla-text-light)',
                  letterSpacing: '0.04em',
                  fontStyle: 'italic',
                }}>
                  {RELATIVE_AGO(u.last_active_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
